import React, { useCallback, useMemo, useState } from "react"
import { StyleSheet } from "~/ui/primitives"
import { observer } from "mobx-react-lite"
import styled, { useTheme } from "~/ui/styled"
import { useSafeAreaInsets } from "~/ui/safe-area"
import { ColumnFlex, StartFlex, CenterFlex, Text } from "~components/El"
import SwitchGroup from "../components/SwitchGroup"
import { SettingGroup, SettingItem } from "../types"
import { AddMethodSettingsScreenProps } from "./types"

const Container = styled.ScrollView`
  flex: 1;
  padding: 0 12px;
`

type AddMethodRule = "phone" | "email" | "qierid" | "qrcode"

type AddMethodOptionState = Record<AddMethodRule, boolean>

const getColumns = ({ option }: { option: any }): SettingGroup[] => {
  return [
    {
      title: "选择添加方式",
      description: "设置他人如何添加您为好友 (可多选)",
      contents: [{
          id: "addMethod.phone",
          type: "navigate",
          title: "手机号",
          content: "允许通过手机号添加",
          suffix: {
            type: "switch",
            value: option.phone,
            onChange: option.setPhone,
          },
          icon: "sj",
          iconColor: "#ff6565",
          iconSize: 16,
          bg: "rgba(255, 101, 101, 0.1)"
        },
        {
          id: "addMethod.email",
          type: "switch",
          title: "邮箱",
          content: "允许通过邮箱地址添加",
          suffix: {
            type: "switch",
            value: option.email,
            onChange: option.setEmail,
          },
          icon: "yx",
          iconColor: "#1bd8cc",
          iconSize: 16,
          bg: "rgba(27, 216, 204, 0.1)",
        },
        {
          id: "online.qierid",
          type: "switch",
          title: "企鹅ID",
          content: "允许通过企鹅ID添加",
          suffix: {
            type: "switch",
            value: option.qierid,
            onChange: option.setQierid,
          },
          icon: "qeid",
          iconColor: "#a507c1",
          iconSize: 16,
          bg: "rgba(165, 7, 193, 0.1)",
        },
        {
          id: "addMethod.qrcode",
          type: "switch",
          title: "二维码",
          content: "允许通过扫描二维码添加",
          suffix: {
            type: "switch",
            value: option.qrcode,
            onChange: option.setQrcode,
          },
          icon: "ewm",
          iconColor: "#65a8ff",
          iconSize: 16,
          bg: "rgba(101, 168, 255, 0.1)",
        },
      ]
    }
  ]
}

const SuffixContent: React.FC<{item: SettingGroup}> = ({ item }) => {
  const theme = useTheme()
  // console.log("item", item)
  const tags = useMemo(() => {
    return item.contents.filter(i => i.suffix.value)
  }, [item])
  return (
    <ColumnFlex
      width="100%"
      padding="12px 0"
      borderTopWidth={StyleSheet.hairlineWidth}
      borderTopColor={theme.colors.border}
      borderTopStyle="solid"
    >
      <Text fontSize={theme.fontSizes.md} color={theme.colors.text} fontWeight="600">已选择的添加方式</Text>
      <StartFlex gap="6px" mt="5px">
        {tags.map((tag)  => (
          <CenterFlex
            key={tag.id}
            height="20px"
            padding="0 10px"
            borderRadius="10px"
            bg={theme.colors.background}
          >
            <Text fontSize={theme.fontSizes.sm} color={theme.colors.secondary}>{tag.title}</Text>
          </CenterFlex>
        ))}
      </StartFlex>
    </ColumnFlex>
  )
}



const AddMethodSettings: React.FC<AddMethodSettingsScreenProps> = () => {
  const insets = useSafeAreaInsets()

  const [state, setState] = useState<AddMethodOptionState>({
    phone: true,
    email: false,
    qierid: false,
    qrcode: true,
  })

  // 至少保留一个开启
  const toggle = useCallback((key: AddMethodRule, val: boolean) => {
    setState(prev => {
      if (!val) {
        const enabledCount = Object.values(prev).filter(Boolean).length
        if (enabledCount <= 1) return prev // 不允许全关
      }
      return { ...prev, [key]: val }
    })
  }, [])


  const option = useMemo(() => {
    return {
      phone: state.phone,
      setPhone: (val: boolean) => toggle("phone", val),

      email: state.email,
      setEmail: (val: boolean) => toggle("email", val),

      qierid: state.qierid,
      setQierid: (val: boolean) => toggle("qierid", val),

      qrcode: state.qrcode,
      setQrcode: (val: boolean) => toggle("qrcode", val),
    }
  }, [state, toggle])


  const onItemClick = useCallback((item: SettingItem) => {
    // console.log("press:", item.id)
  }, [])

  const columns = useMemo(() => getColumns({ option }), [option])
  return (
    <Container style={{ marginBottom: insets.bottom }}>
      <ColumnFlex padding="10px 0">
        {columns.map((item, index) => (
          <SwitchGroup
            key={index}
            item={item}
            suffixContent={<SuffixContent item={item} />}
            onClick={onItemClick}
          />
        ))}
      </ColumnFlex>
    </Container>
  )
}

export default observer(AddMethodSettings)
